/**
 * PostOnboardingChoice - Post-onboarding welcome screen
 * Shows welcome message with provisioned policy count and offers choice between creating a new policy or auditing existing one
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@foundation/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@foundation/components/ui/card';
import { FileText, Search, CheckCircle, Loader2, Home } from 'lucide-react';
import { supabase } from '@foundation/utils/supabaseClient';

export const PostOnboardingChoice: React.FC = () => {
  const navigate = useNavigate();
  const [provisionedCount, setProvisionedCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user's org_id and org name
      const { data: profile } = await supabase
        .schema('altzero')
        .from('base_profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (profile?.tenant_id) {
        // Get org name
        const { data: tenant } = await supabase
          .schema('altzero')
          .from('base_tenants')
          .select('tenant_name')
          .eq('tenant_id', profile.tenant_id)
          .single();

        if (tenant?.tenant_name) {
          setOrgName(tenant.tenant_name);
        }

        // Count documents shared with this org
        const { count, error } = await supabase
          .schema('revisr')
          .from('shared_knowledge_docs')
          .select('*', { count: 'exact', head: true })
          .eq('org_id', profile.tenant_id);

        if (error) {
          console.error('Error counting shared docs:', error);
          setProvisionedCount(0);
        } else {
          setProvisionedCount(count || 0);
        }
      }
    } catch (error) {
      console.error('Error loading provisioned count:', error);
      setProvisionedCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePolicy = () => {
    navigate('/revisr/create-policy');
  };

  const handleAuditPolicy = () => {
    navigate('/revisr/new-audit');
  };

  const handleExploreDashboard = () => {
    navigate('/revisr');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
          <span className="text-muted-foreground">Loading your workspace...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Success Message */}
        <div className="text-center animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-2">
            Welcome to Revisr{orgName && `, ${orgName}`}!
          </h1>
          <p className="text-lg text-muted-foreground">
            {provisionedCount !== null && provisionedCount > 0 ? (
              <>
                Your workspace is ready. We've provisioned <strong>{provisionedCount}</strong>{' '}
                {provisionedCount === 1 ? 'standard document' : 'standard documents'} to help you get started.
              </>
            ) : (
              'Your workspace is ready. Let\'s create your first policy!'
            )}
          </p>
        </div>

        {/* Choice Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Create New Policy */}
          <Card
            className="cursor-pointer transition-all duration-200 border border-border group"
            onClick={handleCreatePolicy}
          >
            <CardHeader>
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 group-hover:bg-blue-100 transition-colors mb-4">
                <FileText className="h-7 w-7 text-blue-600" />
              </div>
              <CardTitle className="text-xl">Create a New Policy</CardTitle>
              <CardDescription className="text-base">
                Start from scratch or use a template to create a new QHSE policy document tailored to your needs.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" size="lg" onClick={handleCreatePolicy}>
                Get Started
              </Button>
            </CardContent>
          </Card>

          {/* Audit Existing Policy */}
          <Card
            className="cursor-pointer transition-all duration-200 border border-border group"
            onClick={handleAuditPolicy}
          >
            <CardHeader>
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-purple-50 group-hover:bg-purple-100 transition-colors mb-4">
                <Search className="h-7 w-7 text-purple-600" />
              </div>
              <CardTitle className="text-xl">Audit an Existing Policy</CardTitle>
              <CardDescription className="text-base">
                Upload and audit an existing policy document against industry standards and regulations.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full" size="lg" onClick={handleAuditPolicy}>
                Start Audit
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Alternative Action */}
        <div className="text-center pt-4">
          <Button variant="ghost" onClick={handleExploreDashboard} className="gap-2">
            <Home className="h-4 w-4" />
            Explore Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};
